<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductCard from '@/Components/ProductCard.vue'
import { Input, Button } from '@/Components/ui'
import { useTranslate } from '@/Composables/useTranslate'

interface Product {
    id: number
    slug: string
    name: Record<string, string>
    short_description?: Record<string, string>
    price_cents: number
    currency: string
    condition: 'new' | 'used'
    is_rentable: boolean
    thumb: string | null
    has_model: boolean
}

interface Category {
    id: number
    slug: string
    name: Record<string, string>
    children: Array<{ id: number; slug: string; name: Record<string, string> }>
}

interface Filters {
    category: string
    condition: string
    min_price: number | null
    max_price: number | null
    q: string
    sort: string
    leasing: boolean
}

const props = defineProps<{
    products: { data: Product[]; links: any[]; total: number; current_page: number; last_page: number }
    categories: Category[]
    filters: Filters
}>()

const { locale, t } = useTranslate()

const q = ref(props.filters.q ?? '')
const minPrice = ref<number | undefined>(props.filters.min_price ?? undefined)
const maxPrice = ref<number | undefined>(props.filters.max_price ?? undefined)
const sort = ref(props.filters.sort)
const filterOpen = ref(false)

// Auto-open the parent of an active child category, otherwise all collapsed
const initialExpanded: Record<number, boolean> = {}
for (const cat of props.categories) {
    initialExpanded[cat.id] = !!cat.children?.some(cc => cc.slug === props.filters.category)
}
const expandedCategories = ref<Record<number, boolean>>(initialExpanded)
function toggleCategoryGroup(id: number) {
    expandedCategories.value[id] = !expandedCategories.value[id]
}

const sectionsOpen = ref({ categories: true, condition: true, price: true, financing: true })
function toggleSection(key: 'categories' | 'condition' | 'price' | 'financing') {
    sectionsOpen.value[key] = !sectionsOpen.value[key]
}

const activeFilterCount = computed(() => {
    let n = 0
    if (props.filters.category) n++
    if (props.filters.condition) n++
    if (props.filters.min_price) n++
    if (props.filters.max_price) n++
    if (props.filters.leasing) n++
    return n
})

function toggleLeasing() {
    apply({ leasing: !props.filters.leasing })
}

watch(filterOpen, (open) => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = open ? 'hidden' : ''
})

let timer: ReturnType<typeof setTimeout> | null = null
function debouncedReload() {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => apply({}), 300)
}
watch([q, minPrice, maxPrice], debouncedReload)
watch(sort, () => apply({}))

function apply(extra: Partial<Filters>) {
    router.get('/shop', {
        ...props.filters,
        q: q.value,
        min_price: minPrice.value || undefined,
        max_price: maxPrice.value || undefined,
        sort: sort.value,
        ...extra,
    }, { preserveScroll: true, preserveState: true, replace: true })
}

function selectCategory(slug: string, parentId?: number) {
    const deselecting = props.filters.category === slug
    apply({ category: deselecting ? '' : slug })
    if (parentId !== undefined) {
        for (const id in expandedCategories.value) expandedCategories.value[Number(id)] = false
        if (!deselecting) expandedCategories.value[parentId] = true
    }
}

function selectCondition(c: string) {
    apply({ condition: props.filters.condition === c ? '' : c })
}

function localized(name: Record<string, string>) {
    return name[locale.value] ?? name.de ?? Object.values(name)[0]
}
</script>

<template>
    <Head :title="t('Shop')" />
    <PublicLayout>
        <section class="px-4 py-8 sm:px-6 lg:px-12 lg:py-10">
            <div class="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 class="text-2xl font-bold sm:text-3xl">{{ t('Shop') }}</h1>
                    <p class="text-sm text-gray-600 dark:text-gray-400">{{ t(':count products', { count: products.total }) }}</p>
                </div>
                <div class="flex items-center gap-2">
                    <select v-model="sort" class="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900">
                        <option value="recent">{{ t('Most recent') }}</option>
                        <option value="newest">{{ t('Newest') }}</option>
                        <option value="price_asc">{{ t('Price ascending') }}</option>
                        <option value="price_desc">{{ t('Price descending') }}</option>
                    </select>
                    <button
                        @click="filterOpen = true"
                        class="relative inline-flex h-10 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 lg:hidden"
                    >
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M6 12h12M10 18h4"/></svg>
                        {{ t('Filter') }}
                        <span v-if="activeFilterCount" class="ml-1 rounded-full bg-brand-600 px-1.5 text-[10px] font-bold text-white">{{ activeFilterCount }}</span>
                    </button>
                </div>
            </div>

            <div class="grid gap-8 lg:grid-cols-[260px_1fr]">
                <aside class="hidden lg:block">
                    <div class="sticky top-20 divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
                        <div class="p-4">
                            <button type="button" @click="toggleSection('categories')" class="mb-3 flex w-full items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <span>{{ t('Categories') }}</span>
                                <svg class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': sectionsOpen.categories }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                            </button>
                            <ul v-if="sectionsOpen.categories" class="space-y-0.5">
                                <li v-for="c in categories" :key="c.id">
                                    <div class="flex items-stretch gap-1">
                                        <button
                                            @click="selectCategory(c.slug, c.children?.length ? c.id : undefined)"
                                            :class="['flex-1 rounded-md px-2.5 py-1.5 text-left text-sm transition', filters.category === c.slug ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800']"
                                        >
                                            {{ localized(c.name) }}
                                        </button>
                                        <button
                                            v-if="c.children?.length"
                                            type="button"
                                            @click="toggleCategoryGroup(c.id)"
                                            :aria-label="expandedCategories[c.id] ? t('Schließen') : t('Öffnen')"
                                            class="flex h-8 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                                        >
                                            <svg class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': expandedCategories[c.id] }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                                        </button>
                                    </div>
                                    <ul v-if="c.children?.length && expandedCategories[c.id]" class="ml-3 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-800">
                                        <li v-for="cc in c.children" :key="cc.id">
                                            <button
                                                @click="selectCategory(cc.slug)"
                                                :class="['w-full rounded-md px-2.5 py-1 text-left text-xs transition', filters.category === cc.slug ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800']"
                                            >
                                                {{ localized(cc.name) }}
                                            </button>
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div class="p-4">
                            <button type="button" @click="toggleSection('condition')" class="mb-3 flex w-full items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <span>{{ t('Zustand') }}</span>
                                <svg class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': sectionsOpen.condition }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                            </button>
                            <div v-if="sectionsOpen.condition" class="space-y-1">
                                <button
                                    v-for="c in [{ val: 'new', label: t('Neuware') }, { val: 'used', label: t('Gebraucht') }]"
                                    :key="c.val"
                                    type="button"
                                    @click="selectCondition(c.val)"
                                    :class="['flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm transition', filters.condition === c.val ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800']"
                                >
                                    {{ c.label }}
                                    <span v-if="filters.condition === c.val" class="text-xs">✓</span>
                                </button>
                            </div>
                        </div>
                        <div class="p-4">
                            <button type="button" @click="toggleSection('financing')" class="mb-3 flex w-full items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <span>{{ t('Finanzierung') }}</span>
                                <svg class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': sectionsOpen.financing }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                            </button>
                            <button
                                v-if="sectionsOpen.financing"
                                type="button"
                                @click="toggleLeasing"
                                :class="['flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition', filters.leasing ? 'bg-brand-600 text-white' : 'border border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800']"
                            >
                                <span>💼 {{ t('Nur leasbare') }}</span>
                                <span v-if="filters.leasing" class="text-xs">✓</span>
                            </button>
                        </div>
                        <div class="p-4">
                            <button type="button" @click="toggleSection('price')" class="mb-3 flex w-full items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <span>{{ t('Preis') }}</span>
                                <svg class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': sectionsOpen.price }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                            </button>
                            <div v-if="sectionsOpen.price" class="flex items-center gap-2">
                                <div class="relative flex-1">
                                    <Input v-model.number="minPrice" type="number" :placeholder="t('from')" class="pr-7" />
                                    <span class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                                </div>
                                <span class="text-gray-400">–</span>
                                <div class="relative flex-1">
                                    <Input v-model.number="maxPrice" type="number" :placeholder="t('to')" class="pr-7" />
                                    <span class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="filters.category || filters.condition || minPrice || maxPrice || filters.leasing" class="p-4">
                            <button
                                @click="apply({ category: '', condition: '', leasing: false }); minPrice = undefined; maxPrice = undefined"
                                class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                            >
                                {{ t('Filter zurücksetzen') }}
                            </button>
                        </div>
                    </div>
                </aside>

                <div>
                    <div class="mb-4 flex flex-wrap gap-2">
                        <div class="relative flex-1 min-w-[180px]">
                            <svg class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                            <Input v-model="q" type="search" :placeholder="t('Shop durchsuchen...')" class="pl-9" />
                        </div>
                        <div class="relative w-28">
                            <Input v-model.number="minPrice" type="number" :placeholder="t('Min €')" class="pr-6" />
                            <span class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                        </div>
                        <div class="relative w-28">
                            <Input v-model.number="maxPrice" type="number" :placeholder="t('Max €')" class="pr-6" />
                            <span class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                        </div>
                    </div>
                    <div v-if="products.data.length" class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        <ProductCard v-for="p in products.data" :key="p.id" :product="p" />
                    </div>
                    <div v-else class="rounded-lg border border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
                        <div class="mb-3 text-4xl">🔎</div>
                        <p class="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                            <template v-if="filters.q">{{ t('Wir haben „:q" nicht im Bestand.', { q: filters.q }) }}</template>
                            <template v-else>{{ t('No products for these filters.') }}</template>
                        </p>
                        <p class="mb-5 text-sm text-gray-600 dark:text-gray-400">{{ t('Stell uns ne kurze Anfrage — Mike besorgt dir das passende Rad mit Angebot.') }}</p>
                        <Link href="/kontakt" class="inline-flex items-center gap-2 rounded-md bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700">
                            ✉ {{ t('Anfrage stellen') }}
                        </Link>
                    </div>

                    <div v-if="products.last_page > 1" class="mt-8 flex flex-wrap gap-1">
                        <Link
                            v-for="link in products.links"
                            :key="link.label"
                            :href="link.url ?? '#'"
                            v-html="link.label"
                            :class="['rounded px-3 py-1.5 text-sm', link.active ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'border border-gray-300 dark:border-gray-700', !link.url ? 'pointer-events-none opacity-50' : '']"
                        />
                    </div>
                </div>
            </div>
        </section>

        <Teleport to="body">
            <transition
                enter-active-class="transition-opacity duration-200"
                enter-from-class="opacity-0" enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-150"
                leave-from-class="opacity-100" leave-to-class="opacity-0"
            >
                <div v-if="filterOpen" class="fixed inset-0 z-[110] lg:hidden">
                    <div class="absolute inset-0 bg-gray-900/40 backdrop-blur-md" @click="filterOpen = false"></div>
                    <transition
                        enter-active-class="transition-transform duration-300 ease-out"
                        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
                        leave-active-class="transition-transform duration-200 ease-in"
                        leave-from-class="translate-y-0" leave-to-class="translate-y-full"
                    >
                        <div v-if="filterOpen" class="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl dark:bg-gray-950">
                            <div class="mb-4 flex items-center justify-between">
                                <h3 class="text-lg font-bold">Filter</h3>
                                <button @click="filterOpen = false" class="rounded-md p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
                                    <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                            </div>
                            <div class="space-y-4 pb-2">
                                <div class="rounded-lg border border-gray-200 dark:border-gray-800">
                                    <button type="button" @click="toggleSection('categories')" class="flex w-full items-center justify-between px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                                        <span>Kategorien<span v-if="filters.category" class="ml-2 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] text-white normal-case">{{ filters.category }}</span></span>
                                        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': sectionsOpen.categories }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                                    </button>
                                    <div v-if="sectionsOpen.categories" class="border-t border-gray-200 p-2 dark:border-gray-800">
                                        <ul class="space-y-1">
                                            <li v-for="c in categories" :key="c.id" class="rounded-md">
                                                <div class="flex items-stretch gap-1">
                                                    <button
                                                        type="button"
                                                        @click="selectCategory(c.slug, c.children?.length ? c.id : undefined)"
                                                        :class="['flex-1 rounded-md px-3 py-2 text-left text-sm transition', filters.category === c.slug ? 'bg-brand-600 font-semibold text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-800']"
                                                    >
                                                        {{ localized(c.name) }}
                                                    </button>
                                                    <button
                                                        v-if="c.children?.length"
                                                        type="button"
                                                        @click="toggleCategoryGroup(c.id)"
                                                        :aria-label="expandedCategories[c.id] ? 'Schließen' : 'Öffnen'"
                                                        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                                                    >
                                                        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': expandedCategories[c.id] }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                                                    </button>
                                                </div>
                                                <ul v-if="c.children?.length && expandedCategories[c.id]" class="ml-3 mt-1 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-800">
                                                    <li v-for="cc in c.children" :key="cc.id">
                                                        <button
                                                            type="button"
                                                            @click="selectCategory(cc.slug)"
                                                            :class="['w-full rounded-md px-3 py-1.5 text-left text-xs transition', filters.category === cc.slug ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800']"
                                                        >
                                                            {{ localized(cc.name) }}
                                                        </button>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="rounded-lg border border-gray-200 dark:border-gray-800">
                                    <button type="button" @click="toggleSection('price')" class="flex w-full items-center justify-between px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                                        <span>Preis (Euro)<span v-if="minPrice || maxPrice" class="ml-2 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] text-white normal-case">{{ minPrice ?? '0' }}–{{ maxPrice ?? '∞' }}</span></span>
                                        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': sectionsOpen.price }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                                    </button>
                                    <div v-if="sectionsOpen.price" class="border-t border-gray-200 p-3 dark:border-gray-800">
                                        <div class="flex gap-2">
                                            <Input v-model.number="minPrice" type="number" :placeholder="t('from')" />
                                            <Input v-model.number="maxPrice" type="number" :placeholder="t('to')" />
                                        </div>
                                    </div>
                                </div>
                                <div class="rounded-lg border border-gray-200 dark:border-gray-800">
                                    <button type="button" @click="toggleSection('financing')" class="flex w-full items-center justify-between px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                                        <span>Finanzierung<span v-if="filters.leasing" class="ml-2 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] text-white normal-case">leasbar</span></span>
                                        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': sectionsOpen.financing }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
                                    </button>
                                    <div v-if="sectionsOpen.financing" class="border-t border-gray-200 p-3 dark:border-gray-800">
                                    <button
                                        type="button"
                                        @click="toggleLeasing"
                                        :class="['flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition', filters.leasing ? 'bg-brand-600 text-white' : 'border border-gray-300 dark:border-gray-700']"
                                    >
                                        <span>💼 Nur leasbare</span>
                                        <span v-if="filters.leasing" class="text-xs">✓</span>
                                    </button>
                                    </div>
                                </div>
                            </div>
                            <div class="sticky bottom-0 -mx-5 -mb-5 mt-6 border-t border-gray-200 bg-white px-5 py-3 dark:border-gray-800 dark:bg-gray-950">
                                <Button class="w-full" @click="filterOpen = false">{{ products.total }} Produkte anzeigen</Button>
                            </div>
                        </div>
                    </transition>
                </div>
            </transition>
        </Teleport>
    </PublicLayout>
</template>
